import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  InnerHero,
  TwoColumnsBlock,
  FullTextWithBgImage,
} from '@/components/sections'
import { Accordion } from '@/components/ui'
import type { AccordionItem } from '@/components/ui'
import { Prefooter, Footer } from '@/components/layout'

function getEthicsItems(t: (k: string) => string): AccordionItem[] {
  return [
    {
      id: 'codice-etico',
      question: t('world.documents.ethicsCode.question'),
      answer: (
        <div className="space-y-5">
          <p>{t('world.documents.ethicsCode.paragraph1')}</p>
          <p>{t('world.documents.ethicsCode.paragraph2')}</p>
          <a
            href="#codice-etico-pdf"
            className="inline-flex items-center gap-3 mt-4 text-[11px] font-medium uppercase tracking-[0.24em] text-bruno pb-2 border-b border-bruno/40 hover:border-argan-deep hover:text-argan-deep transition-colors"
          >
            {t('world.documents.ethicsCode.cta')}
            <span aria-hidden="true">→</span>
          </a>
        </div>
      ),
    },
    {
      id: 'codice-fornitori',
      question: t('world.documents.supplierCode.question'),
      answer: (
        <div className="space-y-5">
          <p>{t('world.documents.supplierCode.paragraph1')}</p>
          <p>{t('world.documents.supplierCode.paragraph2')}</p>
          <a
            href="#codice-fornitori-pdf"
            className="inline-flex items-center gap-3 mt-4 text-[11px] font-medium uppercase tracking-[0.24em] text-bruno pb-2 border-b border-bruno/40 hover:border-argan-deep hover:text-argan-deep transition-colors"
          >
            {t('world.documents.supplierCode.cta')}
            <span aria-hidden="true">→</span>
          </a>
        </div>
      ),
    },
    {
      id: 'whistle-blowing',
      question: t('world.documents.whistleblowing.question'),
      answer: (
        <div className="space-y-5">
          <p>{t('world.documents.whistleblowing.paragraph1')}</p>
          <p>{t('world.documents.whistleblowing.paragraph2')}</p>
          <a
            href="#whistleblowing-portal"
            className="inline-flex items-center gap-3 mt-4 text-[11px] font-medium uppercase tracking-[0.24em] text-bruno pb-2 border-b border-bruno/40 hover:border-argan-deep hover:text-argan-deep transition-colors"
          >
            {t('world.documents.whistleblowing.cta')}
            <span aria-hidden="true">→</span>
          </a>
        </div>
      ),
    },
  ]
}

export function IlMondoNashiArganPage() {
  const { t } = useTranslation('common')
  const ethicsItems = getEthicsItems(t)

  return (
    <main>
      <InnerHero
        eyebrow={t('world.heroIntro.eyebrow')}
        title={t('world.heroIntro.title')}
        titleItalic={t('world.heroIntro.titleItalic')}
        body={t('world.heroIntro.body')}
        breadcrumbs={[
          { label: t('world.heroIntro.breadcrumbHome'), href: '/it' },
          { label: t('world.heroIntro.breadcrumbCurrent') },
        ]}
      />

      {/* Filosofia / brand intro — full text */}
      <section className="relative bg-cream py-24 md:py-36 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-10 right-[-2%] select-none font-display italic text-argan/[0.06]"
          style={{ fontSize: 'clamp(12rem, 26vw, 26rem)', lineHeight: 0.9 }}
        >
          n
        </div>

        <div className="relative nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1 }}
              className="lg:col-span-4"
            >
              <span className="nashi-eyebrow">{t('world.philosophySection.eyebrow')}</span>
              <p className="mt-6 font-display italic text-argan-deep text-2xl md:text-3xl leading-snug">
                {t('world.philosophySection.tagline')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="lg:col-span-8 space-y-6 text-bruno-soft text-base md:text-lg leading-relaxed"
            >
              <p>
                <span className="font-display italic text-bruno text-2xl md:text-3xl block mb-4">
                  {t('world.philosophySection.brandName')}
                </span>
                {t('world.philosophySection.paragraph1')}
              </p>
              <p>{t('world.philosophySection.paragraph2')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amore per la natura — cinematic */}
      <FullTextWithBgImage
        image="/images/nashi-argan-natural.jpg"
        imageAlt={t('world.respectSection.imageAlt')}
        eyebrow={t('world.respectSection.eyebrow')}
        titleLine1={t('world.respectSection.titleLine1')}
        titleLine2={t('world.respectSection.titleLine2')}
        titleLine3={t('world.respectSection.titleLine3')}
        body={t('world.respectSection.body')}
        height="cinematic"
        align="center"
      />

      {/* Le linee Nashi */}
      <TwoColumnsBlock
        chapter={t('world.experienceSection.chapter')}
        eyebrow={t('world.experienceSection.eyebrow')}
        titleLine1={t('world.experienceSection.titleLine1')}
        titleLine2={t('world.experienceSection.titleLine2')}
        body={t('world.experienceSection.body')}
        ctaLabel={t('world.experienceSection.ctaLabel')}
        ctaHref="/it/prodotti"
        image="/images/nashi-linee-spring.jpg"
        imageAlt={t('world.experienceSection.imageAlt')}
        caption={t('world.experienceSection.caption')}
      />

      {/* Membri del FAI */}
      <TwoColumnsBlock
        chapter={t('world.faiSection.chapter')}
        eyebrow={t('world.faiSection.eyebrow')}
        titleLine1={t('world.faiSection.titleLine1')}
        titleLine2={t('world.faiSection.titleLine2')}
        body={t('world.faiSection.body')}
        ctaLabel={t('world.faiSection.ctaLabel')}
        ctaHref="https://www.fondoambiente.it/"
        image="/images/fai-corporate-golden-donor.jpg"
        imageAlt={t('world.faiSection.imageAlt')}
        caption={t('world.faiSection.caption')}
        reverse
        bgTone="cream-soft"
      />

      {/* FAI — extended quote */}
      <section className="relative bg-cream py-24 md:py-32 overflow-hidden">
        <div className="nashi-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9 }}
              className="block font-display italic text-argan-deep text-3xl md:text-4xl mb-8"
            >
              ✦
            </motion.span>
            <motion.blockquote
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="font-display italic text-bruno text-xl md:text-2xl leading-relaxed"
            >
              {t('world.faiQuote.quote')}
            </motion.blockquote>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-10 text-bruno-soft text-base md:text-lg leading-relaxed"
            >
              {t('world.faiQuote.body')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Codice Etico — accordion */}
      <section
        id="codice-etico"
        className="relative bg-cream-soft py-24 md:py-36 overflow-hidden"
        aria-label={t('world.ethicsSection.ariaLabel')}
      >
        <div className="nashi-container">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1 }}
            className="max-w-3xl mb-16 md:mb-20"
          >
            <span className="nashi-eyebrow">{t('world.ethicsSection.eyebrow')}</span>
            <h2
              className="mt-6 font-display text-bruno"
              style={{
                fontSize: 'clamp(2.25rem, 4.5vw, 4rem)',
                lineHeight: 1.02,
                letterSpacing: '-0.015em',
              }}
            >
              <span className="block">{t('world.ethicsSection.titleLine1')}</span>
              <span className="block italic font-light text-argan-deep">
                {t('world.ethicsSection.titleLine2')}
              </span>
            </h2>
            <p className="mt-8 max-w-xl text-bruno-soft text-base md:text-lg leading-relaxed">
              {t('world.ethicsSection.body')}
            </p>
          </motion.div>

          <Accordion items={ethicsItems} />
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
